import React, { useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Wand2,
  Loader2,
  Check,
  ArrowRight,
  UploadCloud,
  FileText,
  Sparkles,
  Copy,
  RefreshCw,
  AlertCircle,
  Zap,
  Download,
  Briefcase,
  HelpCircle,
  Award,
  BarChart3,
  ChevronDown,
} from "lucide-react";
import PageHead from "@/components/PageHead";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function JDCustomizationStudio() {
  const { user, refreshBalance } = useAuth();
  const fileInputRef = useRef(null);

  const [resumeText, setResumeText] = useState("");
  const [cvFileName, setCvFileName] = useState("");
  const [isUploading, setIsUploading] = useState(false);

  const [jd, setJd] = useState("");
  const [stage, setStage] = useState("input"); // "input" | "analyzing" | "questions" | "rewriting" | "done"
  const [analysis, setAnalysis] = useState(null);
  const [answers, setAnswers] = useState({});
  const [rewriteResult, setRewriteResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const [regeneratingIndex, setRegeneratingIndex] = useState(null);

  const handleRegenerateAnswer = async (index, item) => {
    const qText = typeof item === "string" ? item : (item.question || item.q || "");
    const gapArea = typeof item === "object" ? item.gap : "";

    setRegeneratingIndex(index);
    try {
      const { data } = await api.post("/ai/regenerate-suggested-answer", {
        resume_text: resumeText,
        job_description: jd,
        question: qText,
        gap: gapArea,
      });

      if (data?.suggested_answer) {
        const newSuggested = data.suggested_answer;
        setAnalysis((prev) => {
          if (!prev) return prev;
          const updatedQuestions = [...(prev.questions || [])];
          if (typeof updatedQuestions[index] === "object") {
            updatedQuestions[index] = {
              ...updatedQuestions[index],
              suggested_answer: newSuggested,
              example: newSuggested,
            };
          } else {
            updatedQuestions[index] = {
              question: updatedQuestions[index],
              suggested_answer: newSuggested,
            };
          }
          return { ...prev, questions: updatedQuestions };
        });
        toast.success("Regenerated new suggested answer!");
      }
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Could not regenerate suggested answer.");
    } finally {
      setRegeneratingIndex(null);
    }
  };

  // Sync with Career Profile if available
  const loadProfile = () => {
    if (user?.profile) {
      const p = user.profile;
      const skillsStr = Array.isArray(p.skills) ? p.skills.join(", ") : p.skills || "Product Strategy, Analytics";
      const formatted = `${user.name || "Candidate"} — ${p.current_role || p.target_role || "Product Specialist"}
Experience: ${p.years_experience || 3}+ years in ${p.current_role || "Product Management"}.
Preferred Work Model: ${p.work_model || "Remote"} (${Array.isArray(p.locations) ? p.locations.join(", ") : "Global"}).
Target Salary: ${p.target_salary || "$140k"}.
Key Skills & Competencies: ${skillsStr}.
Summary: Proven track record driving product discovery, cross-functional stakeholder alignment, and data-informed roadmap delivery.`;
      setResumeText(formatted);
      setCvFileName("Career_Profile.txt");
      toast.success("Loaded Career Profile details!");
    } else {
      toast("No Career Profile saved yet — upload your CV file");
    }
  };

  // Upload and parse CV file via Backend API
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploading(true);

    // Fast-path for plain text / markdown files
    if (file.name.endsWith(".txt") || file.name.endsWith(".md")) {
      try {
        const text = await file.text();
        if (text.trim()) {
          setResumeText(text);
          setCvFileName(file.name);
          toast.success(`Loaded ${file.name}`);
          setIsUploading(false);
          if (fileInputRef.current) fileInputRef.current.value = "";
          return;
        }
      } catch (_) {}
    }

    const formData = new FormData();
    formData.append("file", file);

    try {
      const { data } = await api.post("/ai/parse-resume", formData);
      if (data.text) {
        setResumeText(data.text);
        setCvFileName(file.name);
        toast.success(`Extracted text from ${file.name}`);
      }
    } catch (err) {
      const msg = formatApiError(err?.response?.data?.detail) || "Could not parse file. You can paste your CV text directly.";
      toast.error(msg);
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  // Step 1 & 2 -> Analyze against CV
  const analyze = async () => {
    if (!resumeText.trim()) {
      toast.error("Please upload or select your CV first (Step 1).");
      return;
    }
    if (!jd.trim()) {
      toast.error("Please enter the target Job Description (Step 2).");
      return;
    }

    setStage("analyzing");
    try {
      const { data } = await api.post("/ai/jd-customize", {
        resume_text: resumeText,
        job_description: jd,
      });
      setAnalysis(data);
      setStage("questions");
      toast.success("Gap analysis complete! Review your identified gaps & questions.");
      setTimeout(() => {
        document.getElementById("step-gaps-section")?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 150);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Analysis failed. Please try again.");
      setStage("input");
    }
  };

  // Step 4 -> Generate rewritten customized CV
  const generate = async () => {
    const answeredList = Object.values(answers).filter((v) => v && v.trim().length > 0);
    if (answeredList.length === 0) {
      toast.error("Answer at least one question so your customized CV has authentic proof.");
      return;
    }

    setStage("rewriting");
    try {
      const { data } = await api.post("/ai/jd-rewrite", {
        resume_text: resumeText,
        job_description: jd,
        answers: answers,
      });
      setRewriteResult(data);
      setStage("done");
      refreshBalance?.();
      toast.success("Customized CV & enhanced bullet points generated!");
      setTimeout(() => {
        document.getElementById("step-result-section")?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 150);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Could not generate rewrite.");
      setStage("questions");
    }
  };

  const copyCustomized = () => {
    if (!rewriteResult) return;
    const textToCopy = `TAILORED PROFESSIONAL SUMMARY:
${rewriteResult.tailored_summary}

HIGH-IMPACT BULLET POINTS (MAPPED TO JD):
${(rewriteResult.enhanced_bullets || []).map((b) => `• ${b}`).join("\n")}

ORIGINAL VS CUSTOMIZED HIGHLIGHT:
Original: ${rewriteResult.original_snippet}
Enhanced: ${rewriteResult.customized_snippet}`;

    navigator.clipboard.writeText(textToCopy);
    setCopied(true);
    toast.success("Copied tailored CV content to clipboard!");
    setTimeout(() => setCopied(false), 2000);
  };

  const [selectedTemplate, setSelectedTemplate] = useState("modern_sans");
  const [isExporting, setIsExporting] = useState(false);

  const exportCustomizedResume = async (fmt = "pdf") => {
    if (!rewriteResult) return;
    setIsExporting(true);
    const userName = user?.name || "Candidate";
    const roleTarget = user?.profile?.target_role || "Target Role";

    try {
      const response = await api.post(
        "/ai/export-tailored-cv",
        {
          user_name: userName,
          target_role: roleTarget,
          email: user?.email || "candidate@example.com",
          summary: rewriteResult.tailored_summary,
          enhanced_bullets: rewriteResult.enhanced_bullets || [],
          gap_categories: analysis?.gap_categories || [],
          original_resume_text: resumeText,
          full_tailored_cv: rewriteResult.full_tailored_cv || "",
          template: selectedTemplate,
          format: fmt,
        },
        { responseType: "blob" }
      );

      const mimeMap = {
        pdf: "application/pdf",
        docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        txt: "text/plain;charset=utf-8",
      };

      const blob = new Blob([response.data], { type: mimeMap[fmt] || "application/octet-stream" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      const safeName = userName.replace(/\s+/g, "_");
      link.download = `${safeName}_Tailored_CV.${fmt}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success(`Exported Tailored CV as .${fmt.toUpperCase()}`);
    } catch (err) {
      toast.error(`Could not export .${fmt.toUpperCase()}. Please try again.`);
    } finally {
      setIsExporting(false);
    }
  };

  const questions = analysis?.questions || [];

  return (
    <div className="max-w-6xl mx-auto pb-16 px-2 sm:px-4">
      <PageHead
        label="CV Customization Studio"
        title="Your Experience Is Unique. Your CV Should Be Too."
        sub="Discover exact qualification gaps, answer targeted questions, and generate an interview-backed tailored CV."
      />

      {/* Stepwise Single Column Workflow */}
      <div className="space-y-8 mt-6">

        {/* STEP 1: Select Resume */}
        <div className="card-surface p-6 shadow-card border border-border/90 rounded-2xl">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-4 pb-3 border-b border-border/60">
            <div className="flex items-center gap-2.5">
              <span className="flex h-7 w-7 items-center justify-center rounded-full bg-electric text-white text-xs font-bold shadow-xs">
                1
              </span>
              <div>
                <h3 className="font-heading text-sm sm:text-base font-bold text-foreground">
                  Select Your Resume
                </h3>
                <p className="text-[11px] sm:text-xs text-muted-foreground">
                  Upload your CV file, load from Career Profile, or paste text.
                </p>
              </div>
            </div>

            <button
              type="button"
              onClick={loadProfile}
              className="text-xs font-semibold text-electric hover:underline flex items-center gap-1"
            >
              <Sparkles className="h-3.5 w-3.5" /> Use Career Profile
            </button>
          </div>

          {/* Drag / Drop / Upload Area */}
          <div className="mb-3.5">
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileUpload}
              accept=".pdf,.docx,.txt"
              className="hidden"
              id="cv-upload-input"
            />
            <label
              htmlFor="cv-upload-input"
              className="flex cursor-pointer items-center justify-between rounded-xl border border-dashed border-border bg-secondary/40 px-4 py-3 text-xs font-medium transition-colors hover:border-electric hover:bg-secondary"
            >
              <div className="flex items-center gap-2.5">
                {isUploading ? (
                  <Loader2 className="h-4 w-4 animate-spin text-electric" />
                ) : (
                  <UploadCloud className="h-4 w-4 text-electric" />
                )}
                <span>
                  {isUploading
                    ? "Extracting CV text with AI…"
                    : cvFileName
                    ? `Selected: ${cvFileName}`
                    : "Upload CV (.pdf, .docx, .txt)"}
                </span>
              </div>
              <span className="rounded-md bg-card px-2.5 py-1 text-[11px] font-semibold text-foreground border border-border shadow-xs">
                Browse File
              </span>
            </label>
          </div>

          {/* Editable Resume Textbox */}
          <textarea
            value={resumeText}
            onChange={(e) => setResumeText(e.target.value)}
            rows={5}
            data-testid="cv-text-input"
            placeholder="Your CV text will appear here. You can also paste or edit it directly…"
            className="w-full resize-none rounded-xl border border-border bg-background p-3.5 text-xs sm:text-sm leading-relaxed outline-none focus:border-electric font-sans"
          />
        </div>

        {/* STEP 2: Target Job Description */}
        <div className="card-surface p-6 shadow-card border border-border/90 rounded-2xl">
          <div className="flex items-center gap-2.5 mb-4 pb-3 border-b border-border/60">
            <span className="flex h-7 w-7 items-center justify-center rounded-full bg-electric text-white text-xs font-bold shadow-xs">
              2
            </span>
            <div>
              <h3 className="font-heading text-sm sm:text-base font-bold text-foreground">
                Target Job Description
              </h3>
              <p className="text-[11px] sm:text-xs text-muted-foreground">
                Paste the requirements, responsibilities, or role description for the position you're targeting.
              </p>
            </div>
          </div>

          <textarea
            value={jd}
            onChange={(e) => setJd(e.target.value)}
            rows={6}
            data-testid="jd-input"
            placeholder="Paste the target job description or requirements here…"
            className="w-full resize-none rounded-xl border border-border bg-background p-3.5 text-xs sm:text-sm leading-relaxed outline-none focus:border-electric font-sans"
          />

          <button
            onClick={analyze}
            disabled={stage === "analyzing" || isUploading}
            data-testid="jd-analyze"
            className="cv-btn-primary mt-4 w-full py-3.5 flex items-center justify-center gap-2 shadow-glow text-sm font-semibold"
          >
            {stage === "analyzing" ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" /> Analyzing qualification gaps…
              </>
            ) : (
              <>
                <Wand2 className="h-4 w-4" /> Analyze Against My CV
              </>
            )}
          </button>
        </div>

        {/* STEP 3 & 4: Identified Gaps & Alignment Questions (shown after analysis) */}
        {(stage === "questions" || stage === "rewriting" || stage === "done") && analysis && (
          <div id="step-gaps-section" className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-300">

            {/* STEP 3: Gaps & Missing Proof Identified */}
            <div className="card-surface p-6 shadow-card border border-border/90 rounded-2xl">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-4 pb-3 border-b border-border/60">
                <div className="flex items-center gap-2.5">
                  <span className="flex h-7 w-7 items-center justify-center rounded-full bg-coral text-white text-xs font-bold shadow-xs">
                    3
                  </span>
                  <div>
                    <h3 className="font-heading text-sm sm:text-base font-bold text-foreground">
                      Gaps & Missing Proof Identified
                    </h3>
                    <p className="text-[11px] sm:text-xs text-muted-foreground">
                      Specific qualification areas required by the JD where your CV lacks explicit evidence.
                    </p>
                  </div>
                </div>

                {analysis.match_score && (
                  <span className="rounded-full bg-coral/10 border border-coral/20 px-3 py-1 text-xs font-bold text-coral">
                    Initial Match: {analysis.match_score}%
                  </span>
                )}
              </div>

              <div className="space-y-2.5">
                {(analysis.mismatches || []).map((m, i) => (
                  <div
                    key={i}
                    className="flex items-start gap-2.5 rounded-xl bg-coral/10 border border-coral/20 p-3 text-xs font-medium text-coral leading-relaxed"
                  >
                    <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
                    <span>{m}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* STEP 4: Targeted Questions & Suggested Answers */}
            <div className="card-surface p-6 shadow-card border border-border/90 rounded-2xl">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-4 pb-3 border-b border-border/60">
                <div className="flex items-center gap-2.5">
                  <span className="flex h-7 w-7 items-center justify-center rounded-full bg-violet text-white text-xs font-bold shadow-xs">
                    4
                  </span>
                  <div>
                    <h3 className="font-heading text-sm sm:text-base font-bold text-foreground">
                      Answer Targeted Alignment Questions
                    </h3>
                    <p className="text-[11px] sm:text-xs text-muted-foreground">
                      Review each question and use or customize the suggested answer grounded in your experience.
                    </p>
                  </div>
                </div>

                <span className="rounded-full bg-violet/10 px-2.5 py-1 text-xs font-bold text-violet">
                  {questions.length} questions
                </span>
              </div>

              {/* Authenticity & Accuracy Notice Banner */}
              <div className="mb-5 flex items-start gap-3 rounded-xl border border-amber-500/25 bg-amber-500/10 p-3.5 text-xs text-amber-900 dark:text-amber-200">
                <AlertCircle className="h-4 w-4 shrink-0 text-amber-500 mt-0.5" />
                <p className="leading-relaxed">
                  <strong>Please provide accurate information based on your real experience.</strong> CVOLVE PRO can help structure and improve your CV, but you are responsible for the accuracy of the information you provide. If you enter false or misleading details, you accept full responsibility for the final content.
                </p>
              </div>

              <div className="space-y-5">
                {questions.map((item, i) => {
                  const qText = typeof item === "string" ? item : (item.question || item.q || "");
                  const suggestedAns = typeof item === "object" ? (item.suggested_answer || item.example || item.answer || "") : "";
                  const gapArea = typeof item === "object" ? item.gap : "";

                  return (
                    <div key={i} className="rounded-2xl border border-border bg-background/60 p-4 space-y-3 shadow-xs">
                      <div className="flex flex-col gap-1">
                        {gapArea && (
                          <span className="self-start text-[10px] font-bold uppercase tracking-wider text-violet/90 bg-violet/10 px-2.5 py-0.5 rounded-md">
                            Gap: {gapArea}
                          </span>
                        )}
                        <p className="text-xs sm:text-sm font-semibold text-foreground leading-snug">
                          <span className="text-violet font-bold mr-1.5">{i + 1}.</span> {qText}
                        </p>
                      </div>

                      <textarea
                        data-testid={`jd-answer-${i}`}
                        rows={3}
                        value={answers[i] || ""}
                        onChange={(e) => setAnswers((a) => ({ ...a, [i]: e.target.value }))}
                        placeholder="Type your authentic answer or click 'Use this answer' below..."
                        className="w-full resize-none rounded-xl border border-border bg-background p-3 text-xs sm:text-sm outline-none focus:border-violet transition-colors leading-relaxed font-sans"
                      />

                      {suggestedAns && (
                        <div className="rounded-xl border border-violet/20 bg-violet/5 p-3.5">
                          <div className="flex flex-wrap items-center justify-between gap-2 mb-1.5">
                            <span className="flex items-center gap-1.5 text-xs font-bold text-violet">
                              <Sparkles className="h-3.5 w-3.5" /> Suggested Answer
                            </span>
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                onClick={() => handleRegenerateAnswer(i, item)}
                                disabled={regeneratingIndex === i}
                                data-testid={`regenerate-suggestion-${i}`}
                                className="flex items-center gap-1.5 rounded-lg border border-violet/30 bg-violet/10 px-2.5 py-1 text-xs font-semibold text-violet hover:bg-violet/20 transition-colors cursor-pointer disabled:opacity-50"
                                title="Re-generate this suggested answer"
                              >
                                <RefreshCw className={`h-3.5 w-3.5 ${regeneratingIndex === i ? "animate-spin text-electric" : ""}`} />
                                <span>{regeneratingIndex === i ? "Regenerating…" : "Regenerate"}</span>
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  setAnswers((a) => ({ ...a, [i]: suggestedAns }));
                                  toast.success("Applied suggested answer!");
                                }}
                                data-testid={`use-suggestion-${i}`}
                                className="flex items-center gap-1 rounded-lg bg-violet/15 px-3 py-1 text-xs font-semibold text-violet hover:bg-violet hover:text-white transition-colors cursor-pointer"
                              >
                                <Check className="h-3.5 w-3.5" /> Use this answer
                              </button>
                            </div>
                          </div>
                          <p className="text-xs text-muted-foreground italic leading-relaxed">
                            "{suggestedAns}"
                          </p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {stage === "rewriting" ? (
                <div className="mt-6 flex flex-col items-center justify-center py-8 text-center rounded-2xl bg-secondary/30 border border-border">
                  <Loader2 className="h-9 w-9 animate-spin text-electric mb-2.5" />
                  <h4 className="font-heading text-base font-bold text-foreground">Rewriting & Aligning CV…</h4>
                  <p className="mt-1 text-xs text-muted-foreground max-w-sm">
                    Weaving your authentic experience answers directly into tailored bullet points matching the JD.
                  </p>
                </div>
              ) : (
                <button
                  onClick={generate}
                  data-testid="jd-generate"
                  className="cv-btn-primary mt-6 w-full py-3.5 flex items-center justify-between px-6 shadow-glow text-sm font-semibold"
                >
                  <span className="flex items-center gap-2">
                    Generate Tailored CV Content <ArrowRight className="h-4 w-4" />
                  </span>
                  <span className="flex items-center gap-1 rounded-full bg-white/20 px-2.5 py-0.5 text-xs font-bold text-white backdrop-blur-xs">
                    <Zap className="h-3 w-3 fill-white" /> 3 Credits
                  </span>
                </button>
              )}
            </div>
          </div>
        )}

        {/* STEP 5: Results & Export Options */}
        {stage === "done" && rewriteResult && (
          <div id="step-result-section" className="card-surface p-6 shadow-card border border-border/90 rounded-2xl space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-300">
            <div className="flex flex-wrap items-center justify-between gap-3 pb-3 border-b border-border/60">
              <div className="flex items-center gap-3">
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-electric text-white text-xs font-bold shadow-xs">
                  5
                </span>
                <div>
                  <h3 className="font-heading text-sm sm:text-base font-bold text-foreground">
                    Your Tailored CV Results
                  </h3>
                  <p className="text-[11px] sm:text-xs text-muted-foreground">
                    Authentic, interview-verified bullet points and professional summary aligned to the target JD.
                  </p>
                </div>
              </div>

              {/* Match Score Upgrade Badge */}
              <div className="flex items-center gap-2 rounded-full bg-lime/10 border border-lime/20 px-3.5 py-1 text-xs font-bold text-lime-600 dark:text-lime">
                <Check className="h-4 w-4" />
                <span>Alignment: {analysis?.match_score || 74}% → {rewriteResult.ats_score || 95}%</span>
              </div>
            </div>

            {/* Template Selector & Downloads Row */}
            <div className="space-y-4 bg-secondary/30 p-4 sm:p-5 rounded-2xl border border-border/80">
              <div className="max-w-md">
                <label className="text-xs font-semibold text-foreground mb-1.5 flex items-center gap-1.5">
                  <Sparkles className="h-3.5 w-3.5 text-electric" /> Select Layout Template
                </label>
                <div className="relative">
                  <select
                    value={selectedTemplate}
                    onChange={(e) => setSelectedTemplate(e.target.value)}
                    className="w-full appearance-none rounded-xl border border-border bg-card px-4 py-3 text-xs sm:text-sm font-medium text-foreground outline-none transition-all hover:border-electric/50 focus:border-electric focus:ring-2 focus:ring-electric/20 shadow-xs cursor-pointer pr-10"
                    data-testid="template-selector"
                  >
                    <option value="modern_sans">⚡ Modern Tech (Product Standard)</option>
                    <option value="classic_serif">📜 Executive Serif (Classic Standard)</option>
                  </select>
                  <ChevronDown className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-3 pt-2">
                <button
                  onClick={() => exportCustomizedResume("pdf")}
                  disabled={isExporting}
                  className="cv-btn-primary py-3 px-5 text-xs sm:text-sm font-semibold flex items-center justify-center gap-2 shadow-glow"
                  data-testid="export-pdf-btn"
                >
                  {isExporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4 text-rose-400" />}
                  <span>Download Resume (PDF)</span>
                </button>

                <button
                  onClick={() => exportCustomizedResume("docx")}
                  disabled={isExporting}
                  className="rounded-xl border border-border bg-card hover:bg-secondary text-foreground py-3 px-5 text-xs sm:text-sm font-semibold flex items-center justify-center gap-2 transition-all shadow-xs"
                  data-testid="export-docx-btn"
                >
                  {isExporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4 text-sky-400" />}
                  <span>Download Resume (DOCX)</span>
                </button>

                <button
                  onClick={() => {
                    setStage("questions");
                    document.getElementById("step-gaps-section")?.scrollIntoView({ behavior: "smooth", block: "start" });
                  }}
                  className="rounded-xl border border-border bg-card hover:bg-secondary text-foreground py-3 px-4 text-xs sm:text-sm font-medium flex items-center justify-center gap-1.5 transition-all shadow-xs ml-auto"
                  title="Regenerate or Refine"
                >
                  <RefreshCw className="h-4 w-4 text-indigo-400" />
                  <span>Regenerate Resume</span>
                </button>
              </div>
            </div>

            {/* Preview Content */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <h4 className="flex items-center gap-2 font-heading text-sm font-bold text-foreground">
                  <FileText className="h-4 w-4 text-amber-500" /> Preview Content
                </h4>
                <button
                  onClick={copyCustomized}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-electric hover:underline"
                >
                  {copied ? <Check className="h-3.5 w-3.5 text-lime" /> : <Copy className="h-3.5 w-3.5" />}
                  {copied ? "Copied" : "Copy All Text"}
                </button>
              </div>

              <div className="rounded-2xl border border-border bg-background p-4 sm:p-5 text-xs sm:text-sm leading-relaxed whitespace-pre-wrap font-mono max-h-[420px] overflow-y-auto text-foreground/90 shadow-inner">
                {rewriteResult.full_tailored_cv || (
                  `${user?.name || "Candidate"} | ${user?.email || "candidate@example.com"} | ${user?.profile?.target_role || "Target Role"}

SUMMARY:
${rewriteResult.tailored_summary}

KEY ACHIEVEMENTS & BULLET POINTS:
${(rewriteResult.enhanced_bullets || []).map((b) => `• ${b}`).join("\n")}

ORIGINAL EXPERIENCE:
${resumeText}`
                )}
              </div>
            </div>

            {/* ATS Analysis Dashboard */}
            <div className="space-y-3 pt-2">
              <h4 className="flex items-center gap-2 font-heading text-sm font-bold text-foreground">
                <BarChart3 className="h-4 w-4 text-electric" /> ATS Analysis
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="card-surface p-4 rounded-2xl border border-border/80 shadow-xs flex flex-col justify-between">
                  <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider">Target ATS</p>
                  <p className="text-2xl sm:text-3xl font-bold text-amber-500 mt-1">100%</p>
                </div>

                <div className="card-surface p-4 rounded-2xl border border-border/80 shadow-xs flex flex-col justify-between">
                  <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider">Measured ATS Score</p>
                  <p className="text-2xl sm:text-3xl font-bold text-electric mt-1">{rewriteResult.ats_score || 95}%</p>
                  <div className="w-full bg-secondary rounded-full h-1.5 mt-2">
                    <div className="bg-electric h-1.5 rounded-full" style={{ width: `${rewriteResult.ats_score || 95}%` }} />
                  </div>
                </div>

                <div className="card-surface p-4 rounded-2xl border border-border/80 shadow-xs flex flex-col justify-between">
                  <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider">Keyword Match</p>
                  <p className="text-2xl sm:text-3xl font-bold text-amber-500 mt-1">{rewriteResult.keyword_match || 100}%</p>
                  <div className="w-full bg-secondary rounded-full h-1.5 mt-2">
                    <div className="bg-amber-500 h-1.5 rounded-full" style={{ width: `${rewriteResult.keyword_match || 100}%` }} />
                  </div>
                </div>
              </div>
            </div>

            {/* Tailored Highlights & Comparisons */}
            <div className="space-y-4 pt-2 border-t border-border/60">
              <div className="space-y-2">
                <p className="label-caps text-electric">Tailored Professional Summary</p>
                <p className="text-xs sm:text-sm text-foreground leading-relaxed bg-secondary/40 p-4 rounded-xl border border-border">
                  {rewriteResult.tailored_summary}
                </p>
              </div>

              <div className="space-y-2.5">
                <p className="label-caps text-violet">Top Enhanced Impact Bullets</p>
                <div className="space-y-2">
                  {(rewriteResult.enhanced_bullets || []).map((bullet, idx) => (
                    <div key={idx} className="rounded-xl border border-border/80 bg-background p-3 text-xs sm:text-sm leading-relaxed">
                      <span className="font-bold text-violet mr-2">•</span>
                      {bullet}
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <p className="label-caps">Original vs Customized Comparison</p>
                <div className="space-y-2 text-xs sm:text-sm">
                  <div className="rounded-xl bg-secondary/70 p-3 text-muted-foreground line-through">
                    <span className="font-bold mr-1.5">Original:</span>
                    {rewriteResult.original_snippet}
                  </div>
                  <div className="rounded-xl bg-electric/10 border border-electric/20 p-3 font-medium text-foreground">
                    <span className="font-bold text-electric mr-1.5">Rewritten:</span>
                    {rewriteResult.customized_snippet}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
